/** Animated content area — switches between table and card views, handles loading and empty states. */
import type { ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useDataView } from './DataView';
import { DataViewTable } from './DataViewTable';
import { DataViewCards } from './DataViewCards';

const EASE = [0.16, 1, 0.3, 1] as [number, number, number, number];

interface DataViewContentProps<T> {
  /** Card renderer — if omitted, only table view is used. */
  renderCard?: (item: T) => ReactNode;
  /** Minimum card width for auto-fill grid. @default 280 */
  cardMinWidth?: number;
  /** Custom empty state. */
  renderEmpty?: ReactNode;
  /** Custom loading skeleton. */
  renderLoading?: ReactNode;
  className?: string;
}

// ─── Default loading skeletons ──────────────────────────────────────────────

function CardSkeleton({ cardMinWidth = 280 }: { cardMinWidth?: number }) {
  return (
    <div
      className="grid gap-3"
      style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${cardMinWidth}px, 1fr))` }}
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-[100px] animate-pulse rounded-2xl"
          style={{ background: 'var(--f-bg-sunken)' }}
        />
      ))}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="flex flex-col">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="h-[48px] animate-pulse"
          style={{ background: 'var(--f-bg-sunken)', borderBottom: '1px solid var(--f-border)' }}
        />
      ))}
    </div>
  );
}

// ─── Content ────────────────────────────────────────────────────────────────

export function DataViewContent<T>({
  renderCard,
  cardMinWidth = 280,
  renderEmpty,
  renderLoading,
  className,
}: DataViewContentProps<T>) {
  const { items, viewMode, isLoading, isStale } = useDataView<T>();

  const isCard = viewMode === 'card' && !!renderCard;

  if (isLoading) {
    return (
      <div className={className}>
        {renderLoading ??
          (isCard ? <CardSkeleton cardMinWidth={cardMinWidth} /> : <TableSkeleton />)}
      </div>
    );
  }

  if (items.length === 0 && renderEmpty) {
    return <div className={className}>{renderEmpty}</div>;
  }

  return (
    <div className={className} style={{ opacity: isStale ? 0.6 : 1, transition: 'opacity 200ms' }}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={isCard ? 'card' : 'table'}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.15, ease: EASE }}
        >
          {isCard ? (
            <DataViewCards<T> renderCard={renderCard!} cardMinWidth={cardMinWidth} />
          ) : (
            <DataViewTable />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
